import React from 'react';
import { XIcon, PackageIcon, MapPinIcon, TruckIcon } from 'lucide-react';
interface OrderItem {
  id: number;
  name: string;
  quantity: number;
  price: number;
  image: string;
}
interface Order {
  id: string;
  date: string;
  total: number;
  status: string;
  items: OrderItem[];
  trackingNumber?: string;
  shippingAddress: string;
}
interface OrderDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: Order | null;
  onCancelOrder: (order: Order) => void;
}
export function OrderDetailsModal({
  isOpen,
  onClose,
  order,
  onCancelOrder
}: OrderDetailsModalProps) {
  if (!isOpen || !order) return null;
  const canCancel = order.status === 'Processing' || order.status === 'Delivered';
  return <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" data-id="element-797" data-mp="2be03j6wix9ap">
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl shadow-xl" data-id="element-798" data-mp="1nn46nvd7nnwl">
        <div className="sticky top-0 bg-white flex items-center justify-between p-5 border-b border-gray-200 z-10" data-id="element-799" data-mp="1iojd2esgs2h4">
          <div data-id="element-800" data-mp="3jwmtslgh73bx">
            <h2 className="text-lg font-bold text-gray-900" data-id="element-801" data-mp="3jw39wz79m4v9">{order.id}</h2>
            <p className="text-sm text-gray-600" data-id="element-802" data-mp="3onldlv0oajjx">
              {new Date(order.date).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100" aria-label="Close" data-id="element-803" data-mp="2w0s5grkyehzw">
            <XIcon className="w-5 h-5" data-id="element-804" data-mp="34z46lnjg4y6l" />
          </button>
        </div>

        <div className="p-5 space-y-5" data-id="element-805" data-mp="351yop8ezm6wu">
          <div className="flex items-center justify-between" data-id="element-806" data-mp="39yytlg03p3md">
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-700 text-sm font-medium rounded-full" data-id="element-807" data-mp="k9esxduhbifh">
              <PackageIcon className="w-4 h-4" data-id="element-808" data-mp="p7omjk0k2ayz" />
              {order.status}
            </span>
            <span className="text-xl font-bold text-[#1E2B55]" data-id="element-809" data-mp="2lausmzs8k15m">
              SAR {order.total.toFixed(2)}
            </span>
          </div>

          {order.trackingNumber && <div className="p-4 bg-blue-50 rounded-lg flex items-center gap-2" data-id="element-810" data-mp="2v9jxnrz19r4g">
              <TruckIcon className="w-5 h-5 text-blue-600" data-id="element-811" data-mp="2v7fwviwfl88e" />
              <span className="text-sm text-gray-700" data-id="element-812" data-mp="2qc0j0qyfbcbz">Tracking:</span>
              <span className="text-sm font-medium text-blue-700" data-id="element-813" data-mp="22klc0kvpc6ue">
                {order.trackingNumber}
              </span>
            </div>}

          <div data-id="element-814" data-mp="1xjyu7jt9y7pe">
            <h3 className="text-sm font-bold text-gray-900 mb-3" data-id="element-815" data-mp="2caxywjqzoc3p">Items</h3>
            <div className="space-y-3" data-id="element-816" data-mp="2cd0xhs67jdk1">
              {order.items.map(item => <div key={item.id} className="flex gap-3 p-3 border border-gray-200 rounded-lg" data-id="element-817" data-mp="27in0jlbwcyd9">
                  <img src={item.image} alt={item.name} className="w-16 h-16 rounded-lg object-cover" data-id="element-818" data-mp="1evtwaszatgyi" />
                  <div className="flex-1" data-id="element-819" data-mp="3v9m257weqmjn">
                    <p className="font-medium text-gray-900 line-clamp-2" data-id="element-820" data-mp="3vanoi8ivivgo">
                      {item.name}
                    </p>
                    <p className="text-sm text-gray-600" data-id="element-821" data-mp="3hg4jrqfgjfv7">
                      Qty: {item.quantity}
                    </p>
                  </div>
                  <p className="font-bold text-gray-900" data-id="element-822" data-mp="d3r9mzmzzj41">
                    SAR {item.price.toFixed(2)}
                  </p>
                </div>)}
            </div>
          </div>

          <div data-id="element-823" data-mp="43uimrd4y85t">
            <h3 className="text-sm font-bold text-gray-900 mb-2" data-id="element-824" data-mp="429mwyf34j6d">
              Shipping Address
            </h3>
            <div className="p-3 bg-gray-50 rounded-lg flex items-start gap-2" data-id="element-825" data-mp="90uksr6v6qrn">
              <MapPinIcon className="w-4 h-4 text-gray-500 mt-0.5" data-id="element-826" data-mp="2osigkja56d8v" />
              <p className="text-sm text-gray-700" data-id="element-827" data-mp="2tsd90r9kfaw1">{order.shippingAddress}</p>
            </div>
          </div>

          <div className="flex gap-3 pt-2" data-id="element-828" data-mp="392tfxq1ob35g">
            <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50" data-id="element-829" data-mp="393kzg2hzxafd">
              Close
            </button>
            {canCancel && <button onClick={() => onCancelOrder(order)} className="flex-1 px-4 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700" data-id="element-830" data-mp="2vboa267ppemo">
                {order.status === 'Delivered' ? 'Request Return' : 'Cancel Order'}
              </button>}
          </div>
        </div>
      </div>
    </div>;
}